<template>
  <SoPageWithRightSidePanel v-model="current.isSidePanelCollapsed">
    <PreDefaultNoticeBanner />
    <SoTabs
      v-model="currentTab"
      divider
      class="ma-4"
      data-testid="financials-tabs"
    >
      <SoTab title="Financials Overview">
        <SoContainer fluid>
          <SoRow>
            <SoCol>
              <div class="d-flex justify-space-between align-center mb-4">
                <div class="text-h5">Fuel Payment Summary</div>
                <SoButton
                  title="Take card payment"
                  type="primary"
                  data-testid="financials-moto-payments"
                  data-dd-action-name="motoFinancialsButton"
                  @click="showMakeCardPaymentModal = true"
                />
              </div>
              <FinancialsOverviewWidget
                data-testid="financials-overview"
                class="mb-6"
              />
            </SoCol>
          </SoRow>

          <SoRow v-if="hasActiveDebtPaymentPlan">
            <SoCol>
              <div
                v-if="current.activeDebtPaymentPlan.data?.isDDRA"
                class="text-h5 mb-4"
              >
                Debt Payment Plan - Digital DRA
              </div>
              <div v-else class="text-h5 mb-4">Debt Payment Plan Summary</div>
              <SoAlert
                v-if="current.activeDebtPaymentPlan.data?.isDDRA"
                data-testid="partner-payment-plan-alert"
                class="mx-5 my-5"
                type="warning"
              >
                <SoRow>
                  <SoCol :default-width="8">
                    <h6>
                      This payment plan has been arranged by our partner Digital
                      DRA. The customer's direct debit may be adjusted to
                      accurately cover their fuel cost. To change the customer's
                      payment plan, raise a task to collections.
                    </h6>
                  </SoCol>
                  <SoCol :default-width="4" class="text-right">
                    <SoButton
                      title="Partner payment plans"
                      large
                      data-testid="partner-payment-plan-guide"
                      type="inverted"
                      @click="showPartnerPaymentPlansModal = true"
                    />
                  </SoCol>
                </SoRow>
              </SoAlert>

              <DebtPaymentPlanOverviewWidget
                data-testid="financials-debt-payment-plan"
                class="mb-6"
              />
            </SoCol>
          </SoRow>

          <div
            class="d-flex"
            :class="{ 'flex-row': !isMobile, 'flex-column': isMobile }"
          >
            <div class="ma-3" style="flex-basis: 60%; min-width: 0">
              <PaymentsOverviewWidget class="h-auto" />
            </div>
            <div
              class="ma-3 flex-column d-flex h-50"
              style="flex-basis: 40%; row-gap: 1rem"
            >
              <SmartPayAsYouGoWidget v-if="isSpaygEnabled" />
              <DebtPaymentPlanWidget />
              <DirectDebitMandateWidget />
              <DepositStatusWidget />
            </div>
          </div>
        </SoContainer>
      </SoTab>
      <SoTab title="Transactions">
        <SoContainer fluid>
          <SoRow>
            <SoCol>
              <TransactionsWidget />
            </SoCol>
          </SoRow>
        </SoContainer>
      </SoTab>
    </SoTabs>
    <template #sidePanel>
      <CustomerAccountInfoPanel />
    </template>
    <template #sidePanelRail>
      <CustomerAccountInfoRail />
    </template>

    <MakeCardPaymentFlowModal
      v-if="showMakeCardPaymentModal"
      @close="showMakeCardPaymentModal = false"
    />
    <PartnerPaymentPlansModal
      v-if="showPartnerPaymentPlansModal"
      v-model="showPartnerPaymentPlansModal"
      @close-modal="showPartnerPaymentPlansModal = false"
    />
  </SoPageWithRightSidePanel>
</template>

<script lang="ts">
  import SoCol from "@/components/SoCol.vue"
  import SoContainer from "@/components/SoContainer.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoTab from "@/components/tabs/SoTab.vue"
  import SoTabs from "@/components/tabs/SoTabs.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoPageWithRightSidePanel from "@/components/templates/SoPageWithRightSidePanel.vue"
  import { FinancialsTabs } from "@/constants/finance"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { RouteNames } from "@/types/router"
  import { defineComponent } from "vue"
  import DirectDebitMandateWidget from "../financials/widgets/DirectDebitMandateWidget.vue"
  import PaymentsOverviewWidget from "../financials/widgets/PaymentsOverviewWidget.vue"
  import CustomerAccountInfoPanel from "../overview/CustomerAccountInfoPanel.vue"
  import CustomerAccountInfoRail from "../overview/CustomerAccountInfoRail.vue"
  import DebtPaymentPlanWidget from "./widgets/DebtPaymentPlanWidget.vue"
  import MakeCardPaymentFlowModal from "./modals/MakeCardPaymentFlowModal.vue"
  import FinancialsOverviewWidget from "./widgets/FinancialsOverviewWidget.vue"
  import TransactionsWidget from "./widgets/TransactionsWidget.vue"
  import DebtPaymentPlanOverviewWidget from "./widgets/debtPaymentPlan/DebtPaymentPlanOverviewWidget.vue"
  import PreDefaultNoticeBanner from "../overview/components/PreDefaultNoticeBanner.vue"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import DepositStatusWidget from "./widgets/DepositStatusWidget.vue"
  import SmartPayAsYouGoWidget from "./widgets/SmartPayAsYouGoWidget.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import PartnerPaymentPlansModal from "./modals/PartnerPaymentPlansModal.vue"

  export default defineComponent({
    components: {
      SoContainer,
      SoPageWithRightSidePanel,
      CustomerAccountInfoPanel,
      CustomerAccountInfoRail,
      SoRow,
      SoCol,
      SoTabs,
      SoTab,
      FinancialsOverviewWidget,
      TransactionsWidget,
      DirectDebitMandateWidget,
      PaymentsOverviewWidget,
      DebtPaymentPlanOverviewWidget,
      DebtPaymentPlanWidget,
      MakeCardPaymentFlowModal,
      SoButton,
      PreDefaultNoticeBanner,
      DepositStatusWidget,
      SmartPayAsYouGoWidget,
      SoAlert,
      PartnerPaymentPlansModal,
    },
    data() {
      return {
        currentTab: FinancialsTabs.Overview,
        current: useCurrentAccountStore(),
        showMakeCardPaymentModal: false,
        showPartnerPaymentPlansModal: false,
        FinancialsTabs,
      }
    },
    computed: {
      isMobile() {
        return this.$vuetify.display.mobile
      },
      hasActiveDebtPaymentPlan() {
        return !!this.current.activeDebtPaymentPlan.data
      },
      isSpaygEnabled() {
        return FeaturesService.isFeatureActive("SMART_PAY_AS_YOU_GO")
      },
    },
    watch: {
      currentTab: function (val) {
        switch (val) {
          case FinancialsTabs.Transactions:
            this.$router.push({
              name: RouteNames.CustomerFinancialsTransactions,
            })
            break
          default:
            this.$router.push({ name: RouteNames.CustomerFinancials })
        }
      },
      isSpaygEnabled: {
        handler(val) {
          if (val === undefined) return

          if (!val) this.currentTab = FinancialsTabs.Overview
        },
        immediate: true,
      },
    },
    mounted() {
      const url = this.$route.fullPath

      if (url.includes("transactions")) {
        this.currentTab = FinancialsTabs.Transactions
      }
    },
  })
</script>
